import { SendOTPResult, SubmitOTPResult } from "~/data/auth";
import { openModalV2, closeAllModals, goBack } from "~/shared_states/modal";
import { showSnackBar } from "~/shared_states/snackbar";
import { PhoneModal } from "./fragments/phone_modal";
import { OtpModal } from "./fragments/otp_modal";
import { ProfileModal } from "./fragments/profile_modal";
import { clientRepo } from "~/server/apis/client_repo";
import { Cookie, Header } from "~/types";
import { setCookie } from "~/utils/client_cookie";
import { config } from "~/data/config";
import { loadRecaptcha } from "~/utils/recaptcha";

interface FlowState {
  phoneNumber: string;
  otpToken: string;
  sessionId: string;
}

/**
 * Starts the webstore login flow
 * Opens phone modal → OTP modal → Profile modal (if needed) → calls onLogin
 */
export function startWebstoreLoginFlow(onLogin: () => void): void {
  void loadRecaptcha();
  const state: FlowState = {
    phoneNumber: "",
    otpToken: "",
    sessionId: "",
  };

  const handlePhoneSubmit = async (
    phoneNumber: string
  ): Promise<SendOTPResult> => {
    const result = await clientRepo.generateHubbleOTPViaSMS(phoneNumber);
    state.phoneNumber = phoneNumber;
    state.otpToken = result.otpToken;

    // Open OTP modal on top of phone modal
    openModalV2(
      () => (
        <OtpModal
          phoneNumber={phoneNumber}
          onSubmit={handleOtpSubmit}
          onResend={handleResendOtp}
          onBack={handleBackToPhone}
          onClose={handleBackToPhone}
        />
      ),
      {
        size: "small",
      }
    );

    return result;
  };

  const handleOtpSubmit = async (otp: string): Promise<void> => {
    const result: SubmitOTPResult = await clientRepo.verifyHubbleOTPViaSMS({
      phoneNumber: state.phoneNumber,
      otp: otp,
      otpToken: state.otpToken,
      autoFill: false,
    });
    state.sessionId = result.sessionId;

    setCookie({
      key: Cookie.SessionId,
      value: result.sessionId,
      expiryInMinutes: config.sesssionDurationInMinutes.webstore,
    });

    // Check if profile is complete
    const _profile = await clientRepo.getUserProfile({
      [Header.SessionId]: result.sessionId,
    });

    const profileComplete =
      _profile.firstName != null && _profile.email != null;

    // Close both phone and OTP modals
    await closeAllModals();

    if (profileComplete) {
      onLogin();
    } else {
      // Show profile completion modal
      openModalV2(
        () => (
          <ProfileModal
            onSubmit={handleProfileSubmit}
            onClose={() => {
              goBack();
              onLogin(); // Still complete login even if they close profile
            }}
          />
        ),
        {
          size: "small",
        }
      );
    }
  };

  const handleResendOtp = async (): Promise<SendOTPResult> => {
    const result = await clientRepo.generateHubbleOTPViaSMS(state.phoneNumber);
    state.otpToken = result.otpToken;
    showSnackBar({
      message: "OTP sent successfully",
      level: "success",
    });
    return result;
  };

  const handleBackToPhone = (): void => {
    goBack(); // Close OTP modal, phone modal is still there
  };

  const handleCloseAll = (): void => {
    closeAllModals();
  };

  const handleProfileSubmit = async (
    name: string,
    email: string
  ): Promise<void> => {
    const nameParts = name.split(" ");
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(" ") || "";

    await clientRepo.updateUserProfile(
      {
        firstName: firstName,
        lastName: lastName,
        phoneNumber: state.phoneNumber,
        email: email,
      },
      {
        [Header.SessionId]: state.sessionId,
      }
    );

    showSnackBar({
      message: "Profile updated successfully",
      level: "success",
    });

    await closeAllModals();
    onLogin();
  };

  // Start the flow by opening phone modal
  openModalV2(
    () => <PhoneModal onSubmit={handlePhoneSubmit} onClose={handleCloseAll} />,
    {
      size: "small",
    }
  );
}
